import { api } from '@/lib/api/client';

export type PeriodStatus = 'provisional' | 'closed';
export type RunKind = 'appfolio_ingest' | 'period_close';
export type RunStatus = 'queued' | 'running' | 'succeeded' | 'failed';

export interface Period {
  id: string;
  propertyKey: string;
  propertyName: string;
  /** YYYY-MM */
  month: string;
  status: PeriodStatus;
  closedAt?: string;
  closedByName?: string;
  closeNote?: string;
}

/** What sreg-pipeline records on a run (runs.py / reports/load.py), keyed by file name. */
export interface RunStats {
  loaded?: Record<string, { report_type: string; revision: number; rows: number; unresolved?: Record<string, number> }>;
  failed?: Record<string, string>;
  skipped?: Record<string, string>;
  closed?: boolean;
  dry_run?: string;
  /** The conflict sweep after the load (P2-G): what it raised, what was already open. */
  conflicts?: { raised: string[]; already_open: string[]; resolved_before: string[]; ties_held: string[] };
}

export interface PipelineRun {
  id: string;
  kind: RunKind;
  periodId?: string;
  status: RunStatus;
  triggeredByName?: string;
  startedAt?: string;
  finishedAt?: string;
  createdAt: string;
  params: { dryRun?: boolean; reason?: string };
  stats: RunStats;
  error?: string;
}

export interface RunStarted {
  run: PipelineRun;
  periodStatus: PeriodStatus;
  periodCreated: boolean;
}

export function listPeriods(signal?: AbortSignal): Promise<Period[]> {
  return api.get<Period[]>('/periods', undefined, signal);
}

export function listPeriodRuns(periodId: string, signal?: AbortSignal): Promise<PipelineRun[]> {
  return api.get<PipelineRun[]>(`/periods/${periodId}/runs`, { limit: 200 }, signal);
}

export function getRun(id: string, signal?: AbortSignal): Promise<PipelineRun> {
  return api.get<PipelineRun>(`/pipeline-runs/${id}`, undefined, signal);
}

/** Process: load the month's reports. A closed month needs a note. */
export function processMonth(v: { propertyKey: string; month: string; note?: string }): Promise<RunStarted> {
  return api.post<RunStarted>('/pipeline-runs', {
    kind: 'appfolio_ingest',
    propertyKey: v.propertyKey,
    period: v.month,
    reason: v.note?.trim() || undefined,
  });
}

/** Close: process, then lock the month if every report loaded. Closing again needs a note. */
export function closeMonth(v: { propertyKey: string; month: string; note?: string }): Promise<RunStarted> {
  return api.post<RunStarted>('/periods/close', {
    propertyKey: v.propertyKey,
    period: v.month,
    note: v.note?.trim() || undefined,
  });
}

export const isActive = (r: Pick<PipelineRun, 'status'>) => r.status === 'queued' || r.status === 'running';

/** "2026-09" -> "September 2026" */
export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** The month before today's, as YYYY-MM: the one usually being closed. */
export function previousMonth(now = new Date()): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}
